import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text, Paragraph } = Typography;

// Visual building blocks for every help topic. Used by topic content modules
// to compose graphically rich, on-palette explanations inside the help drawer.

// ── HeroBlock ──────────────────────────────────────────────────────────────

export type HelpAccent = 'ink' | 'violet' | 'green' | 'amber' | 'red';

interface HeroBlockProps {
  // Icon is accepted for backward compatibility with existing topic content
  // but no longer rendered — caller-side imports can remain in place.
  icon?: ReactNode;
  title: string;
  tagline: string;
  accent: HelpAccent;
}

const ACCENT_GRADIENTS: Record<HelpAccent, { light: string; dark: string }> = {
  ink: {
    light: `linear-gradient(135deg, ${colors.ink[100]} 0%, ${colors.ink[50]} 100%)`,
    dark: `linear-gradient(135deg, ${colors.ink[700]}55 0%, ${colors.slate[800]} 100%)`,
  },
  violet: {
    light: `linear-gradient(135deg, ${colors.violet[100]} 0%, ${colors.violet[50]} 100%)`,
    dark: `linear-gradient(135deg, ${colors.violet[700]}55 0%, ${colors.slate[800]} 100%)`,
  },
  green: {
    light: `linear-gradient(135deg, ${colors.green[100]} 0%, ${colors.ink[50]} 100%)`,
    dark: `linear-gradient(135deg, ${colors.green[700]}55 0%, ${colors.slate[800]} 100%)`,
  },
  amber: {
    light: `linear-gradient(135deg, ${colors.amber[100]} 0%, ${colors.ink[50]} 100%)`,
    dark: `linear-gradient(135deg, ${colors.amber[700]}55 0%, ${colors.slate[800]} 100%)`,
  },
  red: {
    light: `linear-gradient(135deg, ${colors.red[100]} 0%, ${colors.ink[50]} 100%)`,
    dark: `linear-gradient(135deg, ${colors.red[700]}55 0%, ${colors.slate[800]} 100%)`,
  },
};

export const HeroBlock = observer(function HeroBlock({ title, tagline, accent }: HeroBlockProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  return (
    <div
      style={{
        background: ACCENT_GRADIENTS[accent][isDark ? 'dark' : 'light'],
        border: `1px solid ${
          isDark
            ? colors.slate[700]
            : accent === 'ink'
            ? colors.ink[200]
            : accent === 'violet'
            ? colors.violet[200]
            : accent === 'green'
            ? colors.green[100]
            : accent === 'amber'
            ? colors.amber[100]
            : colors.red[100]
        }`,
        borderRadius: 14,
        padding: '18px 22px',
        marginBottom: 20,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          letterSpacing: '-0.015em',
          color: isDark ? colors.slate[50] : colors.slate[900],
          marginBottom: 6,
          lineHeight: 1.25,
        }}
      >
        {title}
      </div>
      <Text
        style={{
          fontSize: 13.5,
          lineHeight: 1.55,
          color: isDark ? colors.slate[300] : colors.slate[600],
        }}
      >
        {tagline}
      </Text>
    </div>
  );
});

// ── SectionTitle ───────────────────────────────────────────────────────────

export const SectionTitle = observer(function SectionTitle({ children }: { children: ReactNode }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: isDark ? colors.slate[400] : colors.slate[500],
        marginTop: 22,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
});

// ── StepFlow ───────────────────────────────────────────────────────────────
// Vertical sequence of step cards connected by a gradient line. Each card has
// a numbered/icon badge, a title, and a body.

export interface FlowStep {
  icon: ReactNode;
  title: string;
  body: ReactNode;
  accent?: HelpAccent;
}

export const StepFlow = observer(function StepFlow({ steps }: { steps: FlowStep[] }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  return (
    <div style={{ position: 'relative' }}>
      {steps.map((step, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 14, position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background:
                  step.accent === 'violet'
                    ? isDark ? `${colors.violet[700]}55` : colors.violet[50]
                    : step.accent === 'green'
                    ? isDark ? `${colors.green[700]}55` : colors.green[100]
                    : step.accent === 'amber'
                    ? isDark ? `${colors.amber[700]}55` : colors.amber[100]
                    : isDark ? `${colors.ink[700]}55` : colors.ink[50],
                color:
                  step.accent === 'violet'
                    ? colors.violet[600]
                    : step.accent === 'green'
                    ? colors.green[600]
                    : step.accent === 'amber'
                    ? colors.amber[600]
                    : colors.ink[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                flexShrink: 0,
                fontWeight: 700,
              }}
            >
              {step.icon}
            </div>
            {idx < steps.length - 1 && (
              <div
                style={{
                  width: 2,
                  flex: 1,
                  minHeight: 24,
                  background: `linear-gradient(180deg, ${
                    isDark ? colors.slate[700] : colors.slate[200]
                  }, ${isDark ? colors.slate[700] : colors.slate[100]})`,
                }}
              />
            )}
          </div>
          <div style={{ flex: 1, paddingBottom: idx < steps.length - 1 ? 22 : 0 }}>
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: isDark ? colors.slate[50] : colors.slate[900],
                marginBottom: 4,
                marginTop: 6,
                letterSpacing: '-0.005em',
              }}
            >
              {step.title}
            </div>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.55,
                color: isDark ? colors.slate[300] : colors.slate[600],
              }}
            >
              {step.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

// ── ChainDiagram ───────────────────────────────────────────────────────────
// Horizontal flow of pills connected by arrows. Use for short architectural
// chains like "Capability → SystemPrompt → AgentInstruction".

export interface ChainNode {
  icon?: ReactNode;
  label: string;
  accent?: HelpAccent;
}

export const ChainDiagram = observer(function ChainDiagram({ nodes }: { nodes: ChainNode[] }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        padding: '14px 12px',
        background: isDark ? colors.slate[800] : colors.slate[50],
        border: `1px dashed ${isDark ? colors.slate[700] : colors.slate[300]}`,
        borderRadius: 10,
      }}
    >
      {nodes.map((node, i) => {
        const accent = node.accent ?? 'ink';
        return (
          <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                background:
                  accent === 'violet'
                    ? isDark ? `${colors.violet[700]}55` : colors.violet[50]
                    : accent === 'green'
                    ? isDark ? `${colors.green[700]}55` : colors.green[100]
                    : accent === 'amber'
                    ? isDark ? `${colors.amber[700]}55` : colors.amber[100]
                    : isDark ? `${colors.ink[700]}55` : colors.ink[50],
                color:
                  accent === 'violet'
                    ? colors.violet[700]
                    : accent === 'green'
                    ? colors.green[700]
                    : accent === 'amber'
                    ? colors.amber[700]
                    : colors.ink[700],
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 999,
                border: `1px solid ${
                  accent === 'violet'
                    ? colors.violet[200]
                    : accent === 'green'
                    ? colors.green[100]
                    : accent === 'amber'
                    ? colors.amber[100]
                    : colors.ink[200]
                }`,
              }}
            >
              {node.icon}
              <span>{node.label}</span>
            </div>
            {i < nodes.length - 1 && (
              <RightOutlined
                style={{
                  fontSize: 11,
                  color: isDark ? colors.slate[400] : colors.slate[400],
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});

// ── CodeBlock ──────────────────────────────────────────────────────────────

export const CodeBlock = observer(function CodeBlock({
  code,
  language,
}: {
  code: string;
  language?: string;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  return (
    <div style={{ position: 'relative', marginTop: 4 }}>
      {language && (
        <div
          style={{
            position: 'absolute',
            top: 6,
            right: 10,
            fontSize: 10,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[500] : colors.slate[400],
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {language}
        </div>
      )}
      <pre
        style={{
          margin: 0,
          padding: '14px 16px',
          background: isDark ? colors.slate[900] : colors.slate[50],
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 8,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12.5,
          lineHeight: 1.55,
          color: isDark ? colors.slate[200] : colors.slate[700],
          whiteSpace: 'pre-wrap',
          overflow: 'auto',
        }}
      >
        {code}
      </pre>
    </div>
  );
});

// ── KeyValueList ───────────────────────────────────────────────────────────
// Used for "Field reference" or "Schema" sections.

export interface KeyValueItem {
  key: string;
  value: ReactNode;
}

export const KeyValueList = observer(function KeyValueList({ items }: { items: KeyValueItem[] }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: '4px 14px',
      }}
    >
      {items.map((it, i) => (
        <div
          key={it.key}
          style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr',
            gap: 12,
            padding: '10px 0',
            borderBottom:
              i < items.length - 1
                ? `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`
                : 'none',
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontFamily: "'JetBrains Mono', monospace",
              color: isDark ? colors.slate[300] : colors.slate[600],
              fontWeight: 600,
            }}
          >
            {it.key}
          </span>
          <span style={{ fontSize: 12.5, color: isDark ? colors.slate[200] : colors.slate[700], lineHeight: 1.5 }}>
            {it.value}
          </span>
        </div>
      ))}
    </div>
  );
});

// ── Callout ────────────────────────────────────────────────────────────────

interface CalloutProps {
  tone: 'info' | 'warning' | 'success';
  title?: string;
  children: ReactNode;
}

export const Callout = observer(function Callout({ tone, title, children }: CalloutProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const palette = {
    info: { bg: isDark ? `${colors.ink[700]}33` : colors.ink[50], border: colors.ink[200], fg: colors.ink[700] },
    warning: {
      bg: isDark ? `${colors.amber[700]}33` : colors.amber[100],
      border: colors.amber[600],
      fg: colors.amber[700],
    },
    success: {
      bg: isDark ? `${colors.green[700]}33` : colors.green[100],
      border: colors.green[600],
      fg: colors.green[700],
    },
  }[tone];

  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      {title && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: palette.fg,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.55, color: isDark ? colors.slate[200] : colors.slate[700] }}>
        {children}
      </div>
    </div>
  );
});

// ── RelatedTopics — clickable links to other help drawers ──────────────────

export interface RelatedTopic {
  topic: string;
  label: string;
  icon?: ReactNode;
}

export const RelatedTopics = observer(function RelatedTopics({ items }: { items: RelatedTopic[] }) {
  const { themeStore, helpStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  if (items.length === 0) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 8,
      }}
    >
      {items.map((item) => (
        <button
          key={item.topic}
          onClick={() => helpStore.open(item.topic)}
          style={{
            background: isDark ? colors.slate[800] : '#FFFFFF',
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            borderRadius: 10,
            padding: '10px 12px',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            transition: 'border-color 120ms ease, transform 120ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.ink[500];
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: isDark ? `${colors.violet[700]}55` : colors.violet[50],
              color: colors.violet[600],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {item.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: isDark ? colors.slate[100] : colors.slate[900],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.label}
            </div>
          </div>
          <RightOutlined
            style={{ fontSize: 10, color: isDark ? colors.slate[400] : colors.slate[400] }}
          />
        </button>
      ))}
    </div>
  );
});

// ── Body wrapper to ensure consistent paragraph styling ────────────────────

export const HelpParagraph = observer(function HelpParagraph({ children }: { children: ReactNode }) {
  return (
    <Paragraph style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 12 }}>{children}</Paragraph>
  );
});
